/**
 * Cable profiles -- group 13: catalogue coverage completion.
 *
 * The twelve preceding groups were built brand by brand, and that produced a
 * predictable bias: every group covered loudspeaker cable and interconnect
 * thoroughly, because those are the ranges a cable maker leads with, and
 * covered USB and ethernet thinly, because those sit at the back of the
 * catalogue. The result under-represented two of the five types the database is
 * supposed to cover.
 *
 * This group closes that gap for brands whose network and USB ranges are real
 * products. It deliberately does NOT invent one for every brand that lacks it:
 * many of the boutique makers in this database genuinely build loudspeaker
 * cable and interconnect and nothing else, and manufacturing a plausible model
 * name for them would be a fabrication that no confidence label could repair. A
 * brand absent from this file is absent on purpose.
 *
 * Two levels of certainty are distinguished here, and the difference is carried
 * in `confidence` rather than in prose. MANUFACTURER_SPEC entries are ones where
 * both the product and its designation are established -- Belden part numbers,
 * the Shunyata network tiers, the Kimber braided ranges. ESTIMATED entries are
 * ones where the range is real but the exact model designation is inferred; each
 * says so in its provenance note. Where neither was solid, there is no entry.
 *
 * As everywhere else: USB and ethernet entries describe construction and stop.
 * The engine applies no signal processing to either type, so no entry here
 * quotes a deviation, and none claims an audible character.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

/** Shared closing sentence for packetised links, so the claim is identical everywhere. */
const ETH_TAIL =
  'Ethernet is packetised and checksummed, errors are retransmitted, and a streamer buffers well ahead of playback, so the engine applies no signal processing to this type.';
const USB_TAIL =
  'USB audio to an asynchronous DAC is clocked at the DAC rather than recovered from the cable, and the payload either arrives intact or does not arrive, so the engine applies no signal processing to this type.';

/** Used wherever the range is established but the model designation is inferred. */
const NAME_INFERRED =
  'The range is an established part of this maker catalogue, but the exact model designation recorded here is inferred rather than quoted from a datasheet, so the entry is marked ESTIMATED. Construction follows the maker general description for the range; electrical figures are derived from gauge, material and geometry and none is presented as a manufacturer figure.';

export const CABLES_13: CableProfile[] = [
  // =========================================================================
  // Shunyata Research -- a power and network specialist, so the absence of a
  // network range in the earlier group was a coverage gap rather than a fact.
  // The four tier names are the ones the company uses across its whole
  // catalogue, which is why these carry specification confidence.
  // =========================================================================
  makeCable({
    mfr: 'Shunyata Research', model: 'Venom Ethernet', cableType: 'ethernet',
    price: usd(175, 'APPROXIMATE', 2024, 'Approximate price for a 1 m patch lead.'), priceLengthM: 1,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 100, digitalStandard: 'Cat 6A, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Foil-shielded pairs', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#b0392f', tags: ['shielded', 'entry-range'],
    notes:
      'The entry point of the Shunyata network range: shielded twisted pairs to Cat 6A with the company standard RJ45 hardware. ' +
      ETH_TAIL,
  }),
  makeCable({
    mfr: 'Shunyata Research', model: 'Alpha Ethernet', cableType: 'ethernet',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate price for a 1 m patch lead.'), priceLengthM: 1,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 100, digitalStandard: 'Cat 8 construction, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Individually shielded pairs plus overall screen', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#b0392f', tags: ['shielded', 'cat8'],
    notes: 'Individually screened pairs inside an overall shield, terminated with shielded RJ45 hardware. ' + ETH_TAIL,
  }),
  makeCable({
    mfr: 'Shunyata Research', model: 'Sigma Ethernet', cableType: 'ethernet',
    price: usd(1500, 'APPROXIMATE', 2024, 'Approximate price for a 1 m patch lead.'), priceLengthM: 1,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 100, digitalStandard: 'Cat 8 construction, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Individually shielded pairs plus overall screen', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#b0392f', tags: ['shielded', 'flagship-adjacent'],
    notes:
      'The senior Shunyata network cable below the Omega, built on the same screened-pair construction with heavier hardware. ' +
      ETH_TAIL,
  }),
  makeCable({
    mfr: 'Shunyata Research', model: 'Omega Ethernet', cableType: 'ethernet',
    price: usd(3000, 'APPROXIMATE', 2024, 'Approximate price for a 1 m patch lead.'), priceLengthM: 1,
    country: 'United States', conductor: 'silver-plated-copper', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 100, digitalStandard: 'Cat 8 construction, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Individually shielded pairs plus overall screen', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#b0392f', tags: ['shielded', 'flagship'],
    notes:
      'Flagship of the Shunyata network range. The construction is genuinely elaborate for a patch lead and the price is what it is; the engine still treats the link as a packet pipe, because that is what it is. ' +
      ETH_TAIL,
  }),

  // =========================================================================
  // Belden and Canare -- pro-infrastructure houses whose part numbers are
  // published, tested and unambiguous. These are the reference points against
  // which the boutique cables in this database are usually argued about.
  // =========================================================================
  makeCable({
    mfr: 'Belden', model: '10GXS Cat 6A', cableType: 'ethernet',
    price: usd(60, 'APPROXIMATE', 2024, 'Approximate cost of a 5 m terminated patch lead from bulk cable.'),
    priceLengthM: 5,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 23,
    characteristicOhms: 100, digitalStandard: 'Cat 6A, 10GBASE-T to 100 m',
    dielectric: 'Polyolefin', shielding: 'Unshielded, bonded pairs', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#2f6fb0',
    tags: ['pro-infrastructure', 'bonded-pair', 'certified'],
    notes:
      'Belden bonded-pair Cat 6A, specified and independently certified for 10GBASE-T over a full 100 m channel -- roughly a hundred times the length of any run inside a listening room. The performance envelope here is documented rather than described. ' +
      ETH_TAIL,
    provenanceNote:
      'Category, gauge and channel performance from the Belden datasheet, which unlike almost every audiophile network cable states tested transmission parameters. Electrical figures derived from the stated gauge and geometry.',
  }),
  makeCable({
    mfr: 'Belden', model: '1872A Cat 6A F/UTP', cableType: 'ethernet',
    price: usd(75, 'APPROXIMATE', 2024, 'Approximate cost of a 5 m terminated patch lead from bulk cable.'),
    priceLengthM: 5,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 23,
    characteristicOhms: 100, digitalStandard: 'Cat 6A F/UTP, 10GBASE-T to 100 m',
    dielectric: 'Polyolefin', shielding: 'Overall foil screen', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#2f6fb0', tags: ['pro-infrastructure', 'shielded', 'certified'],
    notes:
      'The foil-screened variant, specified for the same 10GBASE-T channel with added immunity where cable runs share a tray with mains. ' +
      ETH_TAIL,
  }),
  makeCable({
    mfr: 'Canare', model: 'L-4E6S Interconnect', cableType: 'interconnect',
    price: usd(45, 'APPROXIMATE', 2024, 'Approximate cost of a terminated 1 m pair from bulk cable.'),
    priceLengthM: 1,
    country: 'Japan', conductor: 'ofc', geometry: 'star-quad', gauge: 21,
    capacitancePfPerM: 148, electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource:
      'Capacitance from the Canare L-4E6S datasheet (conductor to conductor, star-quad configuration).',
    dielectric: 'Polyethylene', shielding: 'Spiral copper screen', termination: ['XLR', 'RCA', 'TRS'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC', accent: '#8a6f2f',
    tags: ['pro-audio', 'star-quad', 'balanced', 'reference'],
    notes:
      'The star-quad microphone cable that a very large fraction of the world professional audio infrastructure is wired with. Two conductors per polarity in opposing positions cancel magnetically induced hum, which matters far more in a studio than in a listening room. Canare publishes its capacitance, so the interconnect model here uses a stated figure rather than a derived one.',
    provenanceNote:
      'Construction and capacitance from the Canare datasheet. Inductance derived from the star-quad geometry, as Canare does not state it.',
  }),
  makeCable({
    mfr: 'Canare', model: 'Cat 6 RJ45 Assembly', cableType: 'ethernet',
    price: usd(40, 'APPROXIMATE', 2024, 'Approximate price for a 3 m factory assembly.'), priceLengthM: 3,
    country: 'Japan', conductor: 'ofc', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 6, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Overall braid screen', termination: ['RJ45', 'etherCON'],
    confidence: 'MANUFACTURER_SPEC', accent: '#8a6f2f', tags: ['pro-audio', 'shielded', 'road-rated'],
    notes:
      'Screened Cat 6 in a flexible jacket, available with locking etherCON hardware for installations where a patch lead gets stood on. Built for mechanical survival rather than for any claimed effect on the data. ' +
      ETH_TAIL,
  }),

  // =========================================================================
  // Kimber Kable -- the interconnect, mains and USB ranges are as well known
  // as the braided loudspeaker cable, and were missing entirely.
  // =========================================================================
  makeCable({
    mfr: 'Kimber Kable', model: 'Hero', cableType: 'interconnect',
    price: usd(250, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'), priceLengthM: 1,
    country: 'United States', conductor: 'ofc', geometry: 'braided', gauge: 22,
    dielectric: 'Polyethylene and Teflon hybrid', termination: ['RCA', 'XLR'], balanced: true,
    confidence: 'MANUFACTURER_SPEC', accent: '#3f8a5f', tags: ['braided', 'field-geometry'],
    notes:
      'Kimber braided field geometry applied to an interconnect, in single-ended and balanced terminations. Braiding lowers series inductance and raises capacitance relative to a simple twisted pair, and it is the capacitance that the engine folds against the source output impedance.',
  }),
  makeCable({
    mfr: 'Kimber Kable', model: 'Silver Streak', cableType: 'interconnect',
    price: usd(480, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'), priceLengthM: 1,
    country: 'United States', conductor: 'silver-plated-copper', geometry: 'braided', gauge: 22,
    dielectric: 'Teflon', termination: ['RCA', 'XLR'], balanced: true,
    confidence: 'MANUFACTURER_SPEC', accent: '#3f8a5f', tags: ['braided', 'hybrid-conductor'],
    notes:
      'The same braid with silver-plated conductors alternating against copper. Plating changes the resistivity of the outer skin depth, which at line level over a metre is a change of milliohms in a circuit whose source impedance is orders of magnitude larger.',
  }),
  makeCable({
    mfr: 'Kimber Kable', model: 'KCAG', cableType: 'interconnect',
    price: usd(1400, 'APPROXIMATE', 2024, 'Approximate price for a 1 m pair.'), priceLengthM: 1,
    country: 'United States', conductor: 'pure-silver', geometry: 'braided', gauge: 22,
    dielectric: 'Teflon', termination: ['RCA', 'XLR'], balanced: true,
    confidence: 'MANUFACTURER_SPEC', accent: '#3f8a5f', tags: ['braided', 'solid-silver', 'flagship'],
    notes:
      'Solid silver conductors in Teflon, in the Kimber braid. Silver has about eight per cent lower resistivity than copper, which is the whole of the electrical difference the engine can account for at this signal level.',
  }),
  makeCable({
    mfr: 'Kimber Kable', model: 'PK10 Gold', cableType: 'power',
    price: usd(400, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m cord.'), priceLengthM: 1.5,
    country: 'United States', conductor: 'ofc', geometry: 'braided', gauge: 10,
    currentRatingA: 20, dielectric: 'Polyethylene', termination: ['IEC C19', 'NEMA 5-20'],
    confidence: 'MANUFACTURER_SPEC', accent: '#3f8a5f', tags: ['heavy-gauge', 'braided', 'high-current'],
    notes:
      'Braided 10 AWG conductors rated for 20 A, terminated in C19 for amplifiers that draw genuinely heavy current. Heavy gauge reduces voltage sag under transient draw, which is a real effect on the mains side of the transformer and not on the signal side of it.',
  }),
  makeCable({
    mfr: 'Kimber Kable', model: 'KS 2416 USB', cableType: 'usb',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate price for a 1 m cable.'), priceLengthM: 1,
    country: 'United States', conductor: 'pure-silver', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 90, digitalStandard: 'USB 2.0, Type A to Type B',
    dielectric: 'Teflon', shielding: 'Foil and braid', termination: ['USB A', 'USB B'],
    confidence: 'MANUFACTURER_SPEC', accent: '#3f8a5f', tags: ['solid-silver', 'shielded'],
    notes:
      'Solid silver data conductors in Teflon with the power pair run separately, foil and braid screened. ' +
      USB_TAIL,
  }),

  // =========================================================================
  // Established network ranges where the tier name is known.
  // =========================================================================
  makeCable({
    mfr: 'Cardas Audio', model: 'Clear Network', cableType: 'ethernet',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate price for a 1 m patch lead.'), priceLengthM: 1,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Individually screened pairs plus overall braid', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#8a5f2f', tags: ['shielded', 'constant-q-lay'],
    notes: 'Cardas applies its constant-Q lay to the pairs and screens each of them individually. ' + ETH_TAIL,
  }),
  makeCable({
    mfr: 'Atlas Cables', model: 'Mavros Ethernet', cableType: 'ethernet',
    price: usd(500, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'United Kingdom', conductor: 'occ-copper', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Foamed polyethylene', shielding: 'Screened pairs plus overall foil and braid',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#2f7a6f', tags: ['ohno-continuous-cast', 'shielded'],
    notes:
      'Atlas uses OCC copper, drawn so that grain boundaries do not cross the conductor, in a screened Cat 7 build. ' +
      ETH_TAIL,
  }),
  makeCable({
    mfr: 'Atlas Cables', model: 'Hyper Streaming Ethernet', cableType: 'ethernet',
    price: usd(220, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'United Kingdom', conductor: 'ofc', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Screened pairs plus overall foil', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#2f7a6f', tags: ['shielded'],
    notes: 'The mid-range Atlas patch lead, screened per pair with the same RJ45 hardware as the Mavros. ' + ETH_TAIL,
  }),
  makeCable({
    mfr: 'QED', model: 'Reference Ethernet Cat 7', cableType: 'ethernet',
    price: usd(120, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'United Kingdom', conductor: 'ofc', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Individually screened pairs plus overall braid', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#7a2f4f', tags: ['shielded', 'value'],
    notes:
      'Individually screened pairs to Cat 7 with gold-plated RJ45 contacts, at a price where the construction is straightforwardly worth having. ' +
      ETH_TAIL,
  }),
  makeCable({
    mfr: 'Oyaide', model: 'NEO d+ LAN Class B', cableType: 'ethernet',
    price: usd(90, 'APPROXIMATE', 2024, 'Approximate price for a 2 m patch lead.'), priceLengthM: 2,
    country: 'Japan', conductor: 'ofc', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 6A, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Overall foil screen', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#8a2f2f', tags: ['flexible', 'shielded'],
    notes:
      'Oyaide builds the d+ range around jacket flexibility, which is a genuine practical property behind a rack. ' +
      ETH_TAIL,
  }),
  makeCable({
    mfr: 'Pangea Audio', model: 'Premier SE Cat 7', cableType: 'ethernet',
    price: usd(80, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'United States', conductor: 'silver-plated-copper', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Screened pairs plus overall foil', termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC', accent: '#4f6f2f', tags: ['silver-plated', 'shielded', 'value'],
    notes:
      'Silver-plated conductors and full screening at a price that undercuts most of the rest of this list by an order of magnitude. ' +
      ETH_TAIL,
  }),

  // =========================================================================
  // Ranges that exist, with designations inferred rather than quoted. Each of
  // these is ESTIMATED for that reason and says so.
  // =========================================================================
  makeCable({
    mfr: 'Transparent Audio', model: 'High Performance Ethernet', cableType: 'ethernet',
    price: usd(350, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 6A construction, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Screened pairs plus overall foil', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#2f5f8a', tags: ['shielded'],
    notes:
      'Unlike the Transparent analogue ranges this carries no network box, because there is no analogue transfer function in a packet link for a network to shape. ' +
      ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Siltech', model: 'Classic Legend Ethernet', cableType: 'ethernet',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'Netherlands', conductor: 'silver-gold-alloy', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Peek and air', shielding: 'Screened pairs plus overall screen', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#7a7a8a', tags: ['silver-gold', 'shielded'],
    notes:
      'Siltech runs its silver-gold alloy conductors, in which gold fills the crystal boundaries of the drawn silver, into the network range. The metallurgy is genuine and unusual; its consequence for a checksummed packet stream is nil. ' +
      ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Siltech', model: 'Classic Legend USB', cableType: 'usb',
    price: usd(750, 'APPROXIMATE', 2024, 'Approximate price for a 1 m cable.'), priceLengthM: 1,
    country: 'Netherlands', conductor: 'silver-gold-alloy', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 90, digitalStandard: 'USB 2.0, Type A to Type B',
    dielectric: 'Peek', shielding: 'Foil and braid', termination: ['USB A', 'USB B'],
    confidence: 'ESTIMATED', accent: '#7a7a8a', tags: ['silver-gold', 'shielded'],
    notes:
      'Silver-gold data pair held to the 90 ohm differential impedance the USB specification asks for. ' + USB_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Crystal Cable', model: 'Crystal Connect Ethernet', cableType: 'ethernet',
    price: usd(1100, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'Netherlands', conductor: 'silver-gold-alloy', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Kapton and Peek', shielding: 'Screened pairs plus overall screen', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#9a8f7a', tags: ['silver-gold', 'shielded', 'slim'],
    notes:
      'The characteristically slim Crystal construction applied to a patch lead, using the same silver-gold monocrystal conductor as the analogue ranges. ' +
      ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Tellurium Q', model: 'Silver Diamond Ethernet', cableType: 'ethernet',
    price: usd(1300, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'United Kingdom', conductor: 'silver-plated-copper', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Undisclosed', shielding: 'Screened', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#6f6f7a', tags: ['shielded', 'undisclosed-construction'],
    notes:
      'Tellurium Q declines to publish construction details across its entire range, so the conductor and dielectric recorded here are inferred from the company general description and the entry is marked accordingly. ' +
      ETH_TAIL,
    provenanceNote:
      'Tellurium Q publishes no construction or electrical data for any product. Everything here except the category and termination is inferred, and the entry is labelled ESTIMATED for that reason. Nothing is presented as a manufacturer figure. Note also that the range name is not a material statement -- no silver conductor is claimed on the strength of it.',
  }),
  makeCable({
    mfr: 'SAEC', model: 'LAN-7000', cableType: 'ethernet',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 1 m patch lead.'), priceLengthM: 1,
    country: 'Japan', conductor: 'silver-plated-copper', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Screened pairs plus overall braid', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#2f4f8a', tags: ['silver-plated', 'shielded'],
    notes:
      'Silver-plated PC-Triple C conductors in a fully screened Cat 7 build with a substantial connector shell. ' +
      ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Oehlbach', model: 'Matrix Evolution Cat 7', cableType: 'ethernet',
    price: usd(45, 'APPROXIMATE', 2024, 'Approximate price for a 2 m patch lead.'), priceLengthM: 2,
    country: 'Germany', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 100, digitalStandard: 'Cat 7, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Screened pairs plus overall foil', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#2f6f4f', tags: ['shielded', 'value'],
    notes: 'A conventional screened Cat 7 patch lead sold at a conventional price. ' + ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Goldkabel', model: 'Highline LAN', cableType: 'ethernet',
    price: usd(90, 'APPROXIMATE', 2024, 'Approximate price for a 2 m patch lead.'), priceLengthM: 2,
    country: 'Germany', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 100, digitalStandard: 'Cat 7, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Screened pairs plus overall foil', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#8a7a2f', tags: ['shielded'],
    notes:
      'Screened Cat 7 with metal-shelled RJ45 hardware, from a catalogue built around consistent construction across types. ' +
      ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Norstone', model: 'Jura LAN', cableType: 'ethernet',
    price: usd(50, 'APPROXIMATE', 2024, 'Approximate price for a 2 m patch lead.'), priceLengthM: 2,
    country: 'France', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 100, digitalStandard: 'Cat 7, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Overall foil screen', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#4f4f6f', tags: ['shielded', 'value'],
    notes: 'Screened Cat 7 in a flat jacket intended to run under a rug without a visible ridge. ' + ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Ricable', model: 'Magnus LAN', cableType: 'ethernet',
    price: usd(140, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'Italy', conductor: 'ofc', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 8 construction, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Screened pairs plus overall braid', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#6f2f2f', tags: ['shielded', 'cat8'],
    notes:
      'Ricable applies the same screened construction it uses through the rest of the range to a Cat 8 patch lead. ' +
      ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'DH Labs', model: 'Reunion Ethernet', cableType: 'ethernet',
    price: usd(250, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'United States', conductor: 'silver-plated-copper', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Foamed polyethylene', shielding: 'Screened pairs plus overall braid', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#2f6f7a', tags: ['silver-plated', 'shielded'],
    notes:
      'Silver-plated copper in foamed polyethylene, screened per pair, from a maker that publishes more construction detail than most. ' +
      ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Neotech', model: 'NEET LAN', cableType: 'ethernet',
    price: usd(160, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m terminated lead from bulk cable.'),
    priceLengthM: 2,
    country: 'Taiwan', conductor: 'occ-copper', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Polyethylene', shielding: 'Screened pairs plus overall braid', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#7a5f2f',
    tags: ['ohno-continuous-cast', 'shielded', 'bulk-cable'],
    notes:
      'Neotech sells UP-OCC network cable by the metre for people who terminate their own, which is unusual in this category. ' +
      ETH_TAIL,
    provenanceNote:
      'Neotech network cable is sold under NEET part numbers; the specific number is not recorded here because it could not be quoted reliably, so the entry carries the range designation only and is marked ESTIMATED. ' +
      NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Kubala-Sosna', model: 'Realization Ethernet', cableType: 'ethernet',
    price: usd(2200, 'APPROXIMATE', 2024, 'Approximate price for a 1 m patch lead.'), priceLengthM: 1,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Undisclosed', shielding: 'Screened', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#5f2f6f', tags: ['shielded', 'undisclosed-construction'],
    notes:
      'Kubala-Sosna does not publish construction detail, so most of this entry is inferred and labelled as such. ' +
      ETH_TAIL,
    provenanceNote:
      'Kubala-Sosna publishes no conductor, dielectric or electrical data. Only category, termination and range position are known; the rest is inferred and the entry is marked ESTIMATED.',
  }),
  makeCable({
    mfr: 'Kubala-Sosna', model: 'Realization USB', cableType: 'usb',
    price: usd(1800, 'APPROXIMATE', 2024, 'Approximate price for a 1 m cable.'), priceLengthM: 1,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 90, digitalStandard: 'USB 2.0, Type A to Type B',
    dielectric: 'Undisclosed', shielding: 'Screened', termination: ['USB A', 'USB B'],
    confidence: 'ESTIMATED', accent: '#5f2f6f', tags: ['shielded', 'undisclosed-construction'],
    notes: 'As with the rest of the range, construction is not published. ' + USB_TAIL,
    provenanceNote:
      'Construction inferred; Kubala-Sosna publishes no electrical or material data. Marked ESTIMATED rather than presented as specification.',
  }),
  makeCable({
    mfr: 'Audience', model: 'Au24 SX Ethernet', cableType: 'ethernet',
    price: usd(800, 'APPROXIMATE', 2024, 'Approximate price for a 1.5 m patch lead.'), priceLengthM: 1.5,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 24,
    characteristicOhms: 100, digitalStandard: 'Cat 7 construction, RJ45 terminated',
    dielectric: 'Cross-linked polyethylene', shielding: 'Screened pairs', termination: ['RJ45'],
    confidence: 'ESTIMATED', accent: '#2f5f5f', tags: ['shielded'],
    notes: 'The Au24 construction and dielectric carried across into the network range. ' + ETH_TAIL,
    provenanceNote: NAME_INFERRED,
  }),

  // =========================================================================
  // USB ranges from brands that plainly build them.
  // =========================================================================
  makeCable({
    mfr: 'Analysis Plus', model: 'Purple Plus USB', cableType: 'usb',
    price: usd(150, 'APPROXIMATE', 2024, 'Approximate price for a 1 m cable.'), priceLengthM: 1,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 90, digitalStandard: 'USB 2.0, Type A to Type B',
    dielectric: 'Polyethylene', shielding: 'Foil and braid', termination: ['USB A', 'USB B'],
    confidence: 'ESTIMATED', accent: '#5f2f7a', tags: ['hollow-oval', 'shielded'],
    notes:
      'Analysis Plus builds its hollow oval conductor geometry into the USB range. The geometry is a real and unusual piece of manufacturing, aimed at current distribution within the conductor. ' +
      USB_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Acoustic Zen', model: 'Absolute USB', cableType: 'usb',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate price for a 1 m cable.'), priceLengthM: 1,
    country: 'United States', conductor: 'occ-copper', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 90, digitalStandard: 'USB 2.0, Type A to Type B',
    dielectric: 'Teflon', shielding: 'Foil and braid', termination: ['USB A', 'USB B'],
    confidence: 'ESTIMATED', accent: '#2f7a4f', tags: ['zero-crystal-copper', 'shielded'],
    notes: 'Acoustic Zen zero-crystal copper in Teflon with separated power and data conductors. ' + USB_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Morrow Audio', model: 'USB 4', cableType: 'usb',
    price: usd(300, 'APPROXIMATE', 2024, 'Approximate price for a 1 m cable.'), priceLengthM: 1,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 90, digitalStandard: 'USB 2.0, Type A to Type B',
    dielectric: 'Polyethylene', shielding: 'Foil and braid', termination: ['USB A', 'USB B'],
    confidence: 'ESTIMATED', accent: '#7a4f2f', tags: ['multi-strand', 'shielded'],
    notes:
      'Morrow uses large numbers of fine individually insulated strands throughout the range, including here. The model designation refers to the maker own tier numbering and not to the USB4 transfer standard, which this cable does not implement. ' +
      USB_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Vovox', model: 'textura USB', cableType: 'usb',
    price: usd(200, 'APPROXIMATE', 2024, 'Approximate price for a 1 m cable.'), priceLengthM: 1,
    country: 'Switzerland', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 90, digitalStandard: 'USB 2.0, Type A to Type B',
    dielectric: 'Cotton and polyethylene', shielding: 'Foil and braid', termination: ['USB A', 'USB B'],
    confidence: 'ESTIMATED', accent: '#6f6f2f',
    tags: ['solid-core', 'natural-dielectric', 'shielded'],
    notes: 'Vovox uses solid-core conductors and natural-fibre dielectric across the range. ' + USB_TAIL,
    provenanceNote: NAME_INFERRED,
  }),

  // =========================================================================
  // Pro-audio houses missing a mains or USB entry. These are the control
  // cases: correctly rated, certified, and cheap.
  // =========================================================================
  makeCable({
    mfr: 'Klotz', model: 'Pro USB 2.0', cableType: 'usb',
    price: usd(35, 'APPROXIMATE', 2024, 'Approximate price for a 3 m assembly.'), priceLengthM: 3,
    country: 'Germany', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 90, digitalStandard: 'USB 2.0, Type A to Type B',
    dielectric: 'Polyethylene', shielding: 'Foil and braid', termination: ['USB A', 'USB B'],
    confidence: 'ESTIMATED', accent: '#2f6fb0', tags: ['pro-audio', 'shielded', 'road-rated'],
    notes:
      'Built to the USB specification in a jacket meant to survive being coiled and uncoiled nightly. ' + USB_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Klotz', model: 'Mains Lead IEC', cableType: 'power',
    price: usd(25, 'APPROXIMATE', 2024, 'Approximate price for a 3 m lead.'), priceLengthM: 3,
    country: 'Germany', conductor: 'ofc', geometry: 'twisted-pair', gauge: 16,
    currentRatingA: 10, dielectric: 'PVC', termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED', accent: '#2f6fb0', tags: ['pro-audio', 'road-rated'],
    notes:
      'A correctly rated 10 A mains lead. It is in the database because a large amount of professional audio runs on exactly this, which makes it a useful reference point against the boutique cords.',
    provenanceNote:
      'Recorded under a generic descriptor rather than a catalogue number, because the specific part number could not be quoted reliably. Rating and construction are those of a standard 10 A IEC lead. ' +
      NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Sommer Cable', model: 'Elephant Mains', cableType: 'power',
    price: usd(40, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m assembled lead.'), priceLengthM: 3,
    country: 'Germany', conductor: 'ofc', geometry: 'twisted-pair', gauge: 14,
    currentRatingA: 16, dielectric: 'Rubber', termination: ['IEC C13', 'Schuko', 'PowerCon'],
    confidence: 'ESTIMATED', accent: '#4f7a4f', tags: ['pro-audio', 'road-rated', 'heavy-gauge'],
    notes:
      '16 A rated mains cable in a rubber jacket, available with locking PowerCon hardware. Rated, marked and certified, which is the whole specification a mains lead has to meet.',
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Sommer Cable', model: 'Basic HighSpeed USB', cableType: 'usb',
    price: usd(30, 'APPROXIMATE', 2024, 'Approximate price for a 3 m assembly.'), priceLengthM: 3,
    country: 'Germany', conductor: 'ofc', geometry: 'twisted-pair', gauge: 26,
    characteristicOhms: 90, digitalStandard: 'USB 2.0, Type A to Type B',
    dielectric: 'Polyethylene', shielding: 'Foil and braid', termination: ['USB A', 'USB B'],
    confidence: 'ESTIMATED', accent: '#4f7a4f', tags: ['pro-audio', 'shielded'],
    notes: 'Specification-compliant USB 2.0 in a durable jacket. ' + USB_TAIL,
    provenanceNote: NAME_INFERRED,
  }),
  makeCable({
    mfr: 'Blue Jeans Cable', model: 'Power Cord', cableType: 'power',
    price: usd(20, 'APPROXIMATE', 2024, 'Approximate price for a 2 m cord.'), priceLengthM: 2,
    country: 'United States', conductor: 'ofc', geometry: 'twisted-pair', gauge: 14,
    currentRatingA: 15, dielectric: 'PVC', termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC', accent: '#2f5f8a', tags: ['certified', 'value'],
    notes:
      'A UL-listed 14 AWG cord with moulded ends, sold under a plain descriptive name rather than a model designation. Blue Jeans is unusually direct in print about what a mains cord can and cannot do, and this entry is here partly as the control case against the rest of the power category.',
  }),
  makeCable({
    mfr: 'Mogami', model: '3082 Speaker', cableType: 'speaker',
    price: usd(70, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair from bulk cable.'),
    priceLengthM: 3,
    country: 'Japan', conductor: 'ofc', geometry: 'twisted-pair', gauge: 12,
    resistanceOhmPerM: 0.0106, electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Loop resistance from the Mogami datasheet conductor cross-section (2 x 3.3 mm2).',
    dielectric: 'Polyethylene', termination: ['bare', 'banana', 'spade', 'Speakon NL2'],
    confidence: 'MANUFACTURER_SPEC', accent: '#7a2f2f', tags: ['pro-audio', 'heavy-gauge', 'bulk-cable'],
    notes:
      'Heavy 3.3 mm2 conductors giving about 0.011 ohm per metre of loop resistance -- among the lowest in this database. Over a 3 m run that is roughly 0.032 ohm in series with the loudspeaker, which is a small fraction of a dB into 8 ohms and preserves nearly all of the amplifier damping factor.',
    provenanceNote:
      'Cross-section and construction from the Mogami datasheet; loop resistance follows directly from it. Inductance and capacitance derived from the twisted geometry, as Mogami does not publish them for loudspeaker cable.',
  }),
];
